import { computed, ref } from "vue";
import { defineStore } from "pinia";
import dayjs from "dayjs";

import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";
import { getLicencesForOrg } from "@/utils/licenceApi";
import type {
  ConsumerOrganisationLicencesResponse,
  ResellerOrganisationResponse,
} from "@/types/licenceSolutions";
import type { GraphDataType } from "./type";

export type LicencesCollection =
  ConsumerOrganisationLicencesResponse | ResellerOrganisationResponse;

// Only the fields the store actually reads off the licence forms. Kept loose to
// mirror the previously untyped Vuex dispatch contract (consumers pass reactive
// form objects with extra fields such as `switcher`).
type LicenceFormData = {
  seats_purchased?: number | string;
  expires_at?: string | null;
};

type CreateLicencePayload = {
  solution_uuid: string;
  uuid?: string;
  formData?: LicenceFormData;
  // Consumers pass organisation_type; the store ignores it.
  organisation_type?: string;
  shouldCallV1?: boolean;
};

type UpdateLicencePayload = {
  licence_uuid?: string;
  solution_uuid?: string;
  uuid?: string;
  formData?: LicenceFormData;
  shouldCallV1?: boolean;
};

type RemoveLicencePayload = {
  licence_uuid?: string;
  solution_uuid?: string;
  uuid?: string;
  shouldCallV1?: boolean;
};

export const useLicenceStore = defineStore("licence", () => {
  const collection = ref<LicencesCollection | null>(null);
  const graph = ref<GraphDataType | null>(null);

  const getLicenceGraph = computed(() => graph.value?.data);

  const historyDateRange = () => {
    const today = new Date();
    today.setMonth(today.getMonth() - 12);
    const dateTo = `${new Date().getFullYear()}-${(new Date().getMonth() + 1)
      .toString()
      .padStart(2, "0")}`;
    const dateFrom = today.toISOString().slice(0, 7);
    return { dateFrom, dateTo };
  };

  const listAllLicences = async ({ uuid }: { uuid?: string | null }) => {
    const data = (await getLicencesForOrg(uuid)) as LicencesCollection;
    collection.value = data;
    return data;
  };

  const getLicenceHistoryByService = async ({
    service_uuid,
  }: {
    service_uuid: string;
  }) => {
    const { dateFrom, dateTo } = historyDateRange();
    const res = await $http.get(
      `${getApiClientUrl()}/v2/licence/${service_uuid}/history/graph?date_from=${dateFrom}&date_to=${dateTo}`,
    );
    graph.value = res.data;
  };

  const createLicence = async ({
    solution_uuid,
    uuid,
    formData,
    // TODO: This is temporary fix to not call v1 for SEG licence. Will be removed once v1 is switched off https://gitlab.com/cybersentriq/titanhq/dev/scrum/team-sherman/-/issues/2000 is done
    shouldCallV1 = false,
  }: CreateLicencePayload) => {
    const seats_purchased = formData?.seats_purchased
      ? Math.trunc(+formData.seats_purchased)
      : undefined;
    await $http.post(`${getApiClientUrl()}/v2/licence`, {
      solution_uuid: solution_uuid,
      organisation_uuid: uuid,
      seats_purchased: seats_purchased,
      expires_at: dayjs(new Date()).add(1, "year").format("YYYY-MM-DD"),
    });
    //to be removed after total switch to licence v2
    if (shouldCallV1) {
      await $http.post(
        `${getApiClientUrl()}/v1/licence/organisations/${uuid}/licences`,
        {
          service_uuid: solution_uuid,
          seats_purchased: seats_purchased,
        },
      );
    }
  };

  const updateLicence = async ({
    licence_uuid,
    formData,
    solution_uuid,
    uuid,
    // TODO: This is temporary fix to not call v1 for SEG licence. Will be removed once v1 is switched off https://gitlab.com/cybersentriq/titanhq/dev/scrum/team-sherman/-/issues/2000 is done
    shouldCallV1 = false,
  }: UpdateLicencePayload) => {
    const seats_purchased = formData?.seats_purchased
      ? Math.trunc(+formData.seats_purchased)
      : undefined;
    await $http.patch(`${getApiClientUrl()}/v2/licence/${licence_uuid}`, {
      expires_at: dayjs(formData?.expires_at).format("YYYY-MM-DD"),
      seats_purchased: seats_purchased,
    });
    if (shouldCallV1) {
      await $http.patch(
        `${getApiClientUrl()}/v1/licence/organisations/${uuid}/licences/${solution_uuid}`,
        {
          seats_purchased: seats_purchased,
        },
      );
    }
  };

  const removeLicence = async ({
    licence_uuid,
    uuid,
    solution_uuid,
    // TODO: This is temporary fix to not call v1 for SEG licence. Will be removed once v1 is switched off https://gitlab.com/cybersentriq/titanhq/dev/scrum/team-sherman/-/issues/2000 is done
    shouldCallV1 = false,
  }: RemoveLicencePayload) => {
    await $http.delete(`${getApiClientUrl()}/v2/licence/${licence_uuid}`);
    if (shouldCallV1) {
      await $http.delete(
        `${getApiClientUrl()}/v1/licence/organisations/${uuid}/licences/${solution_uuid}`,
      );
    }
  };

  const clearLicenceCache = () => {
    collection.value = null;
    graph.value = null;
  };

  return {
    collection,
    graph,
    getLicenceGraph,
    listAllLicences,
    getLicenceHistoryByService,
    createLicence,
    updateLicence,
    removeLicence,
    clearLicenceCache,
  };
});
